import {
  assignTableAPIs,
  type RowData,
  type Table_Internal,
  type TableFeature,
  type TableFeatures,
} from '@tanstack/react-table';
import type { Virtualizer } from '@tanstack/react-virtual';
import { flushSync } from 'react-dom';

import type { VirtualScrollingFeatureConstructors } from './virtual-scrolling-types.js';
import { devLog } from '../../../../core/utils/logging.js';
import { DATA_TABLE_DEFAULT_SCROLL_OPTIONS } from '../../constants.js';

/**
 * @internal
 */
export function table_registerVirtualizers<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(
  table: Table_Internal<TFeatures, TData>,
  rowVirtualizer: Virtualizer<HTMLDivElement, Element>,
  columnVirtualizer: Virtualizer<HTMLDivElement, Element>,
): void {
  table._rowVirtualizer = rowVirtualizer;
  table._columnVirtualizer = columnVirtualizer;
}

/**
 * @internal
 */
export function table_getRowVirtualizer<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(
  table: Table_Internal<TFeatures, TData>,
): Virtualizer<HTMLDivElement, Element> | undefined {
  return table._rowVirtualizer;
}

/**
 * @internal
 */
export function table_getColumnVirtualizer<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(
  table: Table_Internal<TFeatures, TData>,
): Virtualizer<HTMLDivElement, Element> | undefined {
  return table._columnVirtualizer;
}

/**
 * @internal
 */
export function table_getRowVirtualizationContainer<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(table: Table_Internal<TFeatures, TData>) {
  return table._rowVirtualizer?.options.getScrollElement() ?? undefined;
}

/**
 * @internal
 */
export function table_getRowHeight<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(table: Table_Internal<TFeatures, TData>, rowId: string) {
  if (
    !table._rowVirtualizer ||
    !rowId ||
    table._rowVirtualizer.measurementsCache.length === 0
  ) {
    return 0;
  }

  const virtualRowIndex = table._rowVirtualizer
    .getVirtualItems()
    .find((item) => item.key === rowId)?.index;

  if (virtualRowIndex === undefined) {
    return 0;
  }

  return (
    table._rowVirtualizer.measurementsCache.find(
      (r) => r.index === virtualRowIndex,
    )?.size ?? 0
  );
}
/**
 * @internal
 */
export function table_getRowScrollOffset<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(table: Table_Internal<TFeatures, TData>) {
  return table._rowVirtualizer?.scrollOffset;
}

/**
 * @internal
 */
export function table_scrollToRowOffset<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(table: Table_Internal<TFeatures, TData>, offset: number | undefined | null) {
  if (offset !== undefined && offset !== null) {
    table._rowVirtualizer?.scrollToOffset(
      offset,
      DATA_TABLE_DEFAULT_SCROLL_OPTIONS,
    );
  }
}

/**
 * Scroll to a given `rowId` using `rowVirtualizer.scrollToIndex`
 * @internal
 */
export function table_scrollToRow<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(
  table: Table_Internal<TFeatures, TData>,
  rowId: string,
  align: 'start' | 'center' | 'end' = DATA_TABLE_DEFAULT_SCROLL_OPTIONS.align,
) {
  const rowVirtualizer = table._rowVirtualizer;

  if (!rowVirtualizer) {
    devLog('[Table - VirtualScrolling] No rowVirtualizer was registered');
    return;
  }

  // Helper function to scroll to the row when table is scrollable and rowVirtualizer is available
  const scrollToTargetRow = () => {
    requestAnimationFrame(() => {
      // This code works because `allRows` will contain (only!) expanded subRows on the top level.
      // When scrolling to a subRow, the row has to be expanded beforehand.
      const allRows = table.getRowModel().rows;
      const rowIndex = allRows.findIndex((row) => row.id === rowId);

      if (rowIndex === -1) {
        devLog(`[Table - VirtualScrolling] Row with id: "${rowId}" not found`);
        return;
      }

      rowVirtualizer.scrollToIndex(rowIndex, {
        behavior: DATA_TABLE_DEFAULT_SCROLL_OPTIONS.behavior,
        align: align,
      });
    });
  };

  if (!table.options.enablePagination) {
    // If pagination is not enabled, we can scroll directly.
    scrollToTargetRow();
    return;
  }

  const prePaginationRows = table.getPrePaginatedRowModel().rows;
  const prePaginationRowIndex = prePaginationRows.findIndex(
    (row) => row.id === rowId,
  );

  if (prePaginationRowIndex === -1) {
    devLog(`[Table - VirtualScrolling] Row with id: "${rowId}" not found`);
    return;
  }

  const paginationState = table.atoms.pagination.get();
  const pageSize = paginationState.pageSize;

  if (!Number.isFinite(pageSize) || pageSize <= 0) {
    devLog(
      `[Table - VirtualScrolling] Invalid page size "${String(
        pageSize,
      )}" while scrolling to row with id: "${rowId}"`,
    );
    return;
  }

  const targetPageIndex = Math.floor(prePaginationRowIndex / pageSize);

  if (targetPageIndex !== paginationState.pageIndex) {
    // flushSync forces React to commit the page change synchronously,
    // so scrollToTargetRow can immediately find the new page's rows.
    flushSync(() => {
      table.setPageIndex(targetPageIndex, 'internal');
    });
  }

  scrollToTargetRow();
}

function measureRows<TFeatures extends TableFeatures, TData extends RowData>(
  table: Table_Internal<TFeatures, TData>,
): void {
  // Early exit if the rowVirtualizer or scrollElement are not present
  if (!table._rowVirtualizer?.scrollElement) {
    return;
  }

  // Select the grid defining container.
  const gridContainer = table._rowVirtualizer.scrollElement.querySelector(
    '[style*="grid-template-rows"]',
  );

  // Early exit if gridContainer is not a HTMLElement
  if (gridContainer === null || !(gridContainer instanceof HTMLElement)) {
    return;
  }

  // Store the original grid row template so it can be applied afterwards.
  const originalGridTemplate = gridContainer.style.gridTemplateRows;
  try {
    let templateRows = table.getHeaderGridTemplateRows();
    const rowCount = table._rowVirtualizer.getVirtualItems().length;

    if (rowCount > 0) {
      // Set the row template for body rows to min-content so all rows can be measured by their actual size.
      const bodyRows = `repeat(${rowCount}, min-content)`;
      // Spacer row height can be defaulted to 0 as it's not relevant for the measuring.
      templateRows = `${templateRows} 0px ${bodyRows} [body-end]`;
    }

    gridContainer.style.gridTemplateRows = templateRows;

    // Select all row elements with the data attribute 'data-row'
    const measureRows =
      table._rowVirtualizer.scrollElement.querySelectorAll('[data-row]');

    // Iterate over each row relevant for measuring
    for (const measureRow of Array.from(measureRows)) {
      // Ensure the element is an instance of HTMLElement
      if (!(measureRow instanceof HTMLElement)) {
        continue;
      }
      table._rowVirtualizer.measureElement(measureRow);
    }

    const virtualItems = table._rowVirtualizer.getVirtualItems();
    const virtualItemsLength = virtualItems.length;

    const firstMeasuredRow =
      measureRows.item(0)?.getAttribute('data-row') ?? null;
    const isDifferentRowsVisible =
      firstMeasuredRow !== null &&
      virtualItemsLength > 0 &&
      Number(firstMeasuredRow) !== virtualItems[0].index;

    // If different rows are measured than those that are currently visible or
    // if fewer items are measured than would now fit into the scrollContainer
    // we reset the measurements cache and allow it to remeasure all items again.
    if (isDifferentRowsVisible || measureRows.length < virtualItemsLength) {
      table._rowVirtualizer.measure();
      // Cache was cleared; restore original template and let the virtualizer
      // remeasure from scratch on re-render.
      gridContainer.style.gridTemplateRows = originalGridTemplate;
    } else {
      // Apply the grid template computed from the new measurements. This prevents
      // elements from resizing back to old heights/stale values.
      gridContainer.style.gridTemplateRows = table.getGridTemplateRows();
    }
  } catch {
    // Restore the original grid template on error.
    gridContainer.style.gridTemplateRows = originalGridTemplate;
  }
}

/**
 * @internal
 */
export function table_remeasureRows<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(table: Table_Internal<TFeatures, TData>, containerWidth: number) {
  if (containerWidth === 0) {
    return;
  }

  // Schedule as a microtask to ensure all DOM updates from the current render cycle
  // (including e.g. lineWrap and visibility changes) are committed before measuring.
  queueMicrotask(() => measureRows(table));
}

function measureColumns<TFeatures extends TableFeatures, TData extends RowData>(
  table: Table_Internal<TFeatures, TData>,
  columnIdOrIds: string | string[],
): void {
  // Early exit if the columnVirtualizer or scrollElement are not present
  if (!table._columnVirtualizer?.scrollElement) {
    return;
  }

  // Select the grid defining container.
  const gridContainer = table._columnVirtualizer.scrollElement.querySelector(
    '[style*="grid-template-columns"]',
  );

  // Early exit if gridContainer is not a HTMLElement
  if (gridContainer === null || !(gridContainer instanceof HTMLElement)) {
    return;
  }

  // Store the original grid row template so it can be applied afterwards.
  const originalGridTemplate = gridContainer.style.gridTemplateColumns;
  try {
    const columnIds = Array.isArray(columnIdOrIds)
      ? columnIdOrIds
      : [columnIdOrIds];
    const columnIdsSet = new Set(columnIds);

    // Reset measurements for the specified columns to trigger a remeasure when regenerating the grid template.
    table.resetMeasuredColumnWidths(columnIds);
    gridContainer.style.gridTemplateColumns = table.getGridTemplateColumns();

    // Select all cells in the first body row
    const firstRowCells =
      table._columnVirtualizer.scrollElement.querySelectorAll(
        '[role="rowgroup"]:nth-child(2) [data-row]:first-child [data-columnid]',
      );

    // Iterate over each cell relevant for measuring
    for (const cell of Array.from(firstRowCells)) {
      const columnId = cell instanceof HTMLElement && cell.dataset.columnid;
      // Only measure if the element is an instance of HTMLElement
      // and its columnId is in the list of columnIds to measure
      if (columnId && columnIdsSet.has(columnId)) {
        table._columnVirtualizer.measureElement(cell);
      }
    }

    // assign the updated grid template with the newly measured column widths
    gridContainer.style.gridTemplateColumns = table.getGridTemplateColumns();
  } catch {
    // Restore the original grid template if something fails.
    gridContainer.style.gridTemplateColumns = originalGridTemplate;
  }
}

/**
 * @internal
 */
export function table_remeasureColumn<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(table: Table_Internal<TFeatures, TData>, columnId?: string) {
  if (columnId === undefined || columnId.length === 0) {
    return;
  }

  // Schedule as a microtask to ensure all DOM updates from the current render cycle
  // are committed before measuring.
  queueMicrotask(() => measureColumns(table, columnId));
}

/**
 * @internal
 */
export function table_remeasureAllColumns<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(table: Table_Internal<TFeatures, TData>) {
  const columnIdsToRemeasure = table.getVisibleVirtualColumnIdsWithoutBuiltin();

  if (columnIdsToRemeasure.length === 0) {
    return;
  }

  // Schedule as a microtask to ensure all DOM updates from the current render cycle
  // are committed before measuring.
  queueMicrotask(() => measureColumns(table, columnIdsToRemeasure));
}

/**
 * @internal
 */
export function table_hasColumnCellsRendered<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(table: Table_Internal<TFeatures, TData>, columnId: string) {
  if (!table._columnVirtualizer?.scrollElement) {
    return false;
  }

  return (
    table._columnVirtualizer?.scrollElement.querySelector(
      `[role="cell"][data-columnid="${CSS.escape(columnId)}"]`,
    ) !== null
  );
}

/**
 * @internal
 */
export function table_getMeasuredColumnWidths<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(table: Table_Internal<TFeatures, TData>): Map<string, number> {
  // Save all column widths that have already been measured outside the virtualizer's measurementsCache.
  // This ensures measured widths are available immediately and don't get lost when column visibility changes.
  table._measuredColumnWidths ??= new Map<string, number>();
  return table._measuredColumnWidths;
}

/**
 * @internal
 */
export function table_getMeasuredColumnWidth<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(table: Table_Internal<TFeatures, TData>, columnId: string) {
  return table_getMeasuredColumnWidths(table).get(columnId);
}

/**
 * @internal
 */
export function table_resetMeasuredColumnWidths<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(table: Table_Internal<TFeatures, TData>, ids?: string[]) {
  if (ids) {
    for (const id of ids) {
      table_getMeasuredColumnWidths(table).delete(id);
    }
  } else {
    table_getMeasuredColumnWidths(table).clear();
  }
}

/**
 * @internal
 */
export function table_setMeasuredColumnWidth<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(table: Table_Internal<TFeatures, TData>, id: string, width: number) {
  table_getMeasuredColumnWidths(table).set(id, width);
}

/**
 * @internal
 */
export function table_getHasVirtualizedRows<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(table: Table_Internal<TFeatures, TData>, virtualItemsLength: number) {
  if (virtualItemsLength === undefined || virtualItemsLength === 0) {
    return false;
  }

  const rows = table.getRowModel().rows;

  return rows.length > virtualItemsLength;
}

/**
 * Constructs the virtual scrolling feature for the table.
 * @internal
 */
export function constructVirtualScrollingFeature(): TableFeature<VirtualScrollingFeatureConstructors> {
  return {
    constructTableAPIs: (table) => {
      assignTableAPIs('DataTableVirtualScrollingFeature', table, {
        table_registerVirtualizers: {
          fn: (
            rowVirtualizer: Virtualizer<HTMLDivElement, Element>,
            columnVirtualizer: Virtualizer<HTMLDivElement, Element>,
          ) =>
            table_registerVirtualizers(
              table,
              rowVirtualizer,
              columnVirtualizer,
            ),
        },
        table_getRowVirtualizer: { fn: () => table_getRowVirtualizer(table) },
        table_getColumnVirtualizer: {
          fn: () => table_getColumnVirtualizer(table),
        },
        table_getRowVirtualizationContainer: {
          fn: () => table_getRowVirtualizationContainer(table),
        },
        table_getRowHeight: {
          fn: (rowId: string) => table_getRowHeight(table, rowId),
        },
        table_getRowScrollOffset: { fn: () => table_getRowScrollOffset(table) },
        table_scrollToRowOffset: {
          fn: (offset: number | undefined | null) =>
            table_scrollToRowOffset(table, offset),
        },
        table_scrollToRow: {
          fn: (rowId: string, align: 'start' | 'center' | 'end') =>
            table_scrollToRow(table, rowId, align),
        },
        table_remeasureRows: {
          fn: (containerWidth: number) =>
            table_remeasureRows(table, containerWidth),
          memoDeps: (containerWidth: number) => {
            const expandingState = table.atoms.expanded?.get();
            const computedExpandingState = table.options.enableExpanding
              ? Object.keys(expandingState ?? {}).length !== 0 && expandingState
              : undefined;

            const rowDetailsState = table.options.enableRowDetails
              ? table.atoms.rowDetails?.get()
              : undefined;
            const sortingState = table.options.enableSorting
              ? table.atoms.sorting?.get()
              : undefined;
            return [
              containerWidth,
              computedExpandingState,
              rowDetailsState,
              sortingState,
              table.atoms.columnLineWrap?.get(),
              table.atoms.visualVariantConfig?.get().rowDensity,
              table.atoms.visualVariantConfig?.get().rowSeparation,
              table.atoms.columnSizing?.get(),
              table.atoms.pagination?.get().pageIndex,
              table.atoms.pagination?.get().pageSize,
              table.atoms.columnVisibility?.get(),
              table.atoms.rowOrder?.get(),
              table.options.data,
              table.options.columns,
            ];
          },
        },
        table_remeasureColumn: {
          fn: (columnId?: string) => table_remeasureColumn(table, columnId),
        },
        table_remeasureAllColumns: {
          fn: () => table_remeasureAllColumns(table),
        },
        table_hasColumnCellsRendered: {
          fn: (columnId: string) =>
            table_hasColumnCellsRendered(table, columnId),
        },
        table_getMeasuredColumnWidths: {
          fn: () => table_getMeasuredColumnWidths(table),
        },
        table_getMeasuredColumnWidth: {
          fn: (columnId: string) =>
            table_getMeasuredColumnWidth(table, columnId),
        },
        table_resetMeasuredColumnWidths: {
          fn: (columnIds?: string[]) =>
            table_resetMeasuredColumnWidths(table, columnIds),
        },
        table_setMeasuredColumnWidth: {
          fn: (id: string, width: number) =>
            table_setMeasuredColumnWidth(table, id, width),
        },
        table_getHasVirtualizedRows: {
          fn: (virtualItemsLength: number) =>
            table_getHasVirtualizedRows(table, virtualItemsLength),
          memoDeps: () => [table._rowVirtualizer?.getVirtualItems().length],
        },
      });
    },
  };
}

/**
 * Feature implementation for virtual scrolling.
 * @internal
 */
export const DataTableVirtualScrollingFeature =
  constructVirtualScrollingFeature();
